import { type SkyName } from "@common/utils/mapWeather";

/*
    The light over the battlefield (0.98.6, owner decision, 1 November 2026: "Add shadows + lightning and athmosphere +
    shaders. Shaders has quality (low, medium, high). Shadows high and low or OFF. Lighting and athmosphere also like
    shadows"): the parts without Pixi (tests/src/update0986.test.ts). The sun (the moon at night, the storm's grey
    sky) says which way the shadows fall, how long and how dark they are, and how the shaders light the ground's
    relief; the clouds' shadows drift over the ground with the wind. Settings → Graphics has three choices
    (console/variables.ts): `cv_war_look` the shaders (Low, Medium, High), `cv_shadows` and `cv_lighting` (Off, Low,
    High). See managers/lighting.ts and managers/shadowLook.ts for what's drawn.
*/

export type LightingLevel = "off" | "low" | "high";
export type ShadowLevel = "off" | "low" | "high";
export const LIGHTING_LEVELS: readonly LightingLevel[] = Object.freeze(["off", "low", "high"]);
export const SHADOW_LEVELS: readonly ShadowLevel[] = Object.freeze(["off", "low", "high"]);
export const isLightingLevel = (value: unknown): value is LightingLevel => typeof value === "string" && (LIGHTING_LEVELS as readonly string[]).includes(value);
export const isShadowLevel = (value: unknown): value is ShadowLevel => typeof value === "string" && (SHADOW_LEVELS as readonly string[]).includes(value);

/** the sun as the page draws by it: the shadows' way and the light on the relief */
export interface Sun {
    /** the way shadows fall on screen (radians: 0 to the right, π/2 down the screen) */
    readonly angle: number
    /** how long a shadow is for each unit of its thing's height (game units of shadow per unit up) */
    readonly stretch: number
    /** how dark the shadows are (0 none, 1 black) */
    readonly alpha: number
    /** the shadows' colour (plum at dusk, blue on snow, grey under a storm) */
    readonly tint: number
    /** how soft their edges are (0 hard, 1 soft) */
    readonly soft: number
    /** how strongly the light models the ground's relief and shines on wet mud (0 flat, 1 full) */
    readonly relief: number
    /** the light's own colour on the relief's lit side */
    readonly light: readonly [number, number, number]
}

interface SkyLight extends Sun {
    /** how far the sun moves over a match with the lighting on: the shadows turn this much (radians) and lengthen this much */
    readonly drift: { readonly angle: number, readonly stretch: number }
}

/** a shadow falls down and to the right by day: the same way every player's contact shadow has always fallen */
export const DAY_SHADOW_ANGLE = Math.atan2(1, 0.55);
/** at dusk the sun is low in the lower left: long shadows toward the upper right (1.05's angle, kept) */
export const DUSK_SHADOW_ANGLE = -0.45;

/** a match's length the sun's drift is measured over (seconds: the sun has moved all its way by then) */
export const DRIFT_SECONDS = 540;

/** each sky's light */
export const SKY_LIGHT: Readonly<Record<SkyName, SkyLight>> = Object.freeze({
    // a clear day: the sun high, the shadows short and dark, turning a little as the day goes
    clear: { angle: DAY_SHADOW_ANGLE, stretch: 1.1, alpha: 0.5, tint: 0x0e1210, soft: 0.5, relief: 0.8, light: [1.0, 0.96, 0.86], drift: { angle: 0.35, stretch: 0.35 } },
    // dusk: the sun low in the lower left, long plum shadows that lengthen as it sinks
    dusk: { angle: DUSK_SHADOW_ANGLE, stretch: 2.6, alpha: 0.6, tint: 0x2a1232, soft: 0.65, relief: 1.0, light: [1.2, 0.86, 0.6], drift: { angle: 0.14, stretch: 1.0 } },
    // night: the moon's shadows, faint and cold (the night's dark lies over them: nightLight.ts)
    night: { angle: Math.atan2(-0.6, -0.8), stretch: 1.6, alpha: 0.24, tint: 0x02040c, soft: 0.8, relief: 0.35, light: [0.7, 0.8, 1.0], drift: { angle: 0.3, stretch: 0.2 } },
    // snow: a pale sun, blue shadows on the white
    snow: { angle: 1.1, stretch: 1.3, alpha: 0.4, tint: 0x2a3d60, soft: 0.75, relief: 0.6, light: [0.95, 0.97, 1.05], drift: { angle: 0.3, stretch: 0.3 } },
    // a thunderstorm: no sun, a grey sky all round, the shadows short, soft and faint (the lightning's hard and black)
    storm: { angle: 1.4, stretch: 0.5, alpha: 0.22, tint: 0x080a12, soft: 1.0, relief: 0.3, light: [0.85, 0.9, 1.0], drift: { angle: 0.1, stretch: 0.1 } }
});

/** a lightning strike as the page shows it now: how bright its flash is (0 none, 1 the brightest), and from which way (radians) */
export interface Lightning {
    readonly alpha: number
    readonly angle: number
}

/** a strike's shadows: hard, long and black, away from it */
const LIGHTNING_SUN = Object.freeze({ stretch: 1.8, alpha: 0.72, soft: 0.1, relief: 1.0 });

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const smooth = (t: number): number => {
    const k = Math.min(1, Math.max(0, t));
    return k * k * (3 - 2 * k);
};

/**
 * the sun under `sky` at `seconds` into the match: with the lighting on (`level` not off) it moves over the match (the
 * shadows turn, and lengthen as it sinks), and a lightning strike (`lightning`, the thunderstorm's) throws hard black
 * shadows away from it while it flashes; with the lighting off it stands still where the sky puts it
 */
export function sunAt(sky: SkyName, seconds: number, level: LightingLevel, lightning?: Lightning): Sun {
    const base = SKY_LIGHT[sky];
    let sun: Sun = base;
    if (level !== "off") {
        const t = smooth(Math.max(0, seconds) / DRIFT_SECONDS);
        sun = { ...base, angle: base.angle + base.drift.angle * t, stretch: base.stretch + base.drift.stretch * t };
        const flash = lightning ? Math.min(1, Math.max(0, lightning.alpha)) : 0;
        if (flash > 0.02) {
            const k = smooth(flash * 2);
            sun = {
                ...sun,
                angle: lightning?.angle ?? sun.angle,
                stretch: lerp(sun.stretch, LIGHTNING_SUN.stretch, k),
                alpha: lerp(sun.alpha, LIGHTNING_SUN.alpha, k),
                soft: lerp(sun.soft, LIGHTNING_SUN.soft, k),
                relief: lerp(sun.relief, LIGHTNING_SUN.relief, k),
                tint: k > 0.5 ? 0x000000 : sun.tint
            };
        }
    }
    return sun;
}

/** the sun's direction as a unit vector on screen (the way shadows fall) */
export const sunDirection = (sun: Sun): { readonly x: number, readonly y: number } => ({ x: Math.cos(sun.angle), y: Math.sin(sun.angle) });

/** the clouds' shadows drifting over the ground: how dark, how fast they go with the wind (times its speed), how big */
export interface Clouds {
    readonly alpha: number
    readonly speed: number
    /** one tile of them across this many game units */
    readonly size: number
}
export const CLOUDS: Readonly<Record<SkyName, Clouds>> = Object.freeze({
    clear: { alpha: 0.2, speed: 1.0, size: 360 },
    dusk: { alpha: 0.14, speed: 0.8, size: 420 },
    // (under the night's dark nothing of a cloud's shadow would show)
    night: { alpha: 0, speed: 1, size: 360 },
    snow: { alpha: 0.1, speed: 1.3, size: 500 },
    // (low, ragged and fast)
    storm: { alpha: 0.34, speed: 3.0, size: 280 }
});

/** the clouds' shadows under `sky` at a lighting `level` (none with the lighting off) */
export const cloudsAt = (sky: SkyName, level: LightingLevel): Clouds => level === "off" ? { ...CLOUDS[sky], alpha: 0 } : CLOUDS[sky];

/**
 * the warm glow round a light by day and at dusk (the night's own is nightLight.ts's): how strong a fire's and a
 * lamp's glow show under each sky (0 none)
 */
export const GLOW: Readonly<Record<SkyName, { readonly fire: number, readonly lamp: number, readonly flash: number }>> = Object.freeze({
    clear: { fire: 0.2, lamp: 0, flash: 0.4 },
    dusk: { fire: 0.36, lamp: 0.3, flash: 0.52 },
    // (the night's lights are the night's: nightLight.ts erases the dark round them)
    night: { fire: 0, lamp: 0, flash: 0 },
    // (on the white, a little: a glow on snow is soon a white-out)
    snow: { fire: 0.12, lamp: 0, flash: 0.3 },
    // (the storm's lamps flicker in the storm view: stormView.ts)
    storm: { fire: 0.3, lamp: 0, flash: 0.5 }
});

/** the bloom (the lighting at High, with the shaders): how bright a pixel is before it blooms, how much it blooms */
export const BLOOM = Object.freeze({
    threshold: 0.8,
    /** a soft knee under the threshold (a share of it), so nothing pops in and out of the bloom */
    knee: 0.2,
    strength: 0.32,
    /** the bloom is made at this share of the screen's pixels each way (it's a blur: small is enough, and cheap) */
    scale: 0.25
});

/** how bright a pixel is before it blooms under `sky`: on the snow the ground itself is bright, so only the brightest things bloom there */
export const bloomThreshold = (sky: SkyName): number => (sky === "snow" ? 0.96 : sky === "night" ? 0.72 : BLOOM.threshold);

/** the heat haze over fires (the lighting at High, with the shaders): how far the picture wavers over a fire (screen pixels) */
export const HEAT = Object.freeze({
    wobble: 2.2,
    /** the heat map is drawn at this share of the screen's pixels each way */
    scale: 0.25,
    /** how far round a burning patch the air shimmers (game units) */
    reach: 9
});

/** the sun's rays at dusk (the lighting at High): how strong, how fast they turn */
export const RAYS = Object.freeze({ alpha: 0.12, turn: 0.012 });
